/**
 * The client's end of the game server link: `srv` in main.ts (null offline).
 *
 *   srv.send({t: ...})            an intent, typed (protocol.ts ClientMsg)
 *   srv.sendRaw({t: 'held', ...}) an intent protocol.ts does not type yet
 *   srv.on('slots', m => ...)     a fact from the server, any `t`, typed when known
 *   srv.onBinary(tag, f)          binary frames by their first byte
 *   srv.dig / place / chat        the intents with prediction built in
 *
 * Unknown message types are not an error: they go to whoever subscribed and
 * are otherwise dropped. No three.js and no DOM here (Node-testable); the
 * world it edits is behind `WorldPort`, the page glue is client/install.ts.
 */

import {
  PROTOCOL_VERSION, BIN_CHUNK, MF_SNEAK, MF_SPRINT, MF_SWIM, MF_FLY, MF_GROUND, MF_USING, MF_GLIDE, decodeChunk,
  type ClientMsg, type ServerMsg, type PlayerRow,
} from '../server/protocol';
import type { DimensionId } from '../world/dimension';
import { SnapshotBuffer, INTERP_DELAY_MS } from '../net/interp';
import { emptyPlayerState, type PlayerState } from '../net/codec';
import { Connection, type SocketCtor } from './connection';

export type Welcome = Extract<ServerMsg, { t: 'welcome' }>;
type MsgOf<T extends string> = Extract<ServerMsg, { t: T }>;
/** A message whose type this build does not know: read its fields as needed. */
export interface AnyMsg { t: string; [k: string]: unknown }
type Handler = (m: never) => void;

/** What the session needs from the client's copy of the world (ChunkManager in remote mode). */
export interface WorldPort {
  /** Apply the player's own edit now, with the local edit rules. Returns [x, y, z, oldId, ...] of every cell it changed. */
  predict(x: number, y: number, z: number, id: number): number[];
  /** Put predicted cells back (the server refused). */
  revert(cells: readonly number[]): void;
  /** A fact from the server: the cell holds `id`. Must not echo or save. */
  applyServer(x: number, y: number, z: number, id: number): void;
  /** Several applyServer calls as one batch. */
  batch(fn: () => void): void;
  chunk(dim: DimensionId, cx: number, cz: number, blocks: Uint16Array): void;
  unload(cx: number, cz: number): void;
  /** A (re)login: forget every chunk and show dimension `dim`. */
  reset(dim: DimensionId): void;
}

/** The local player as `move` reports it (player/player.ts Player fits; position = feet). */
export interface LocalPose {
  position: { x: number; y: number; z: number }; yaw: number; pitch: number;
  sneaking: boolean; sprinting: boolean; swimming: boolean; flying: boolean; onGround: boolean;
  /** Drawing a bow, eating, shield up. */
  usingItem?: boolean;
  /** Elytra flight (the server's fall and speed rules need it). */
  glider?: { gliding: boolean };
}

/** Another player, shaped for net/remotePlayers.ts (RemotePlayerView). */
export interface ServerPlayerView { id: string; name: string; state: PlayerState; v: Float64Array; ping: number; isHost: boolean }

interface Remote {
  name: string;
  buf: SnapshotBuffer;
  state: PlayerState;
  v: Float64Array;
  s: Float64Array;
  lastAt: number;
  /** Walk cycle, advanced by the distance covered between rows. */
  swing: number;
  amt: number;
  lx: number; lz: number;
  flags: number;
}

export type ServerStatus = 'connecting' | 'online' | 'reconnecting' | 'refused';

export interface ServerSessionOptions {
  url: string;
  key: string;
  name: string;
  /** Chunk radius to ask for (the server clamps it). */
  view: number;
  /** Tests: a WebSocket implementation and a clock. */
  socket?: SocketCtor;
  now?: () => number;
}

const MOVE_INTERVAL_MS = 50;
/** A player with no row for this long is not drawn (left the dimension, or out of the server's range). */
const STALE_MS = 1500;
/** Predictions never answered (connection lost) are forgotten after this many newer ones. */
const MAX_PENDING = 256;

export class ServerSession {
  status: ServerStatus = 'connecting';
  /** Why the server refused us (status 'refused'). */
  refusal = '';
  /** Own player id. '' until the first welcome. */
  id = '';
  seed = 0;
  wgv = 0;
  dim: DimensionId = 'overworld';
  /** Day fraction 0..1, as the server last said. */
  time = 0;
  /** The last welcome (null before the first). */
  welcome: Welcome | null = null;
  /** Everyone logged in, including us: id -> name. */
  readonly names = new Map<string, string>();
  name: string;
  view: number;
  /** The client's world. Until attach() gives the real one, chunks are held in memory. */
  private world: WorldPort = new HeldWorld();
  /**
   * `move` is only sent while this is true. The page clears it while the
   * player is frozen waiting for terrain (login, teleport) so the server never
   * sees a position it did not give us, then sets it once spawned.
   */
  spawned = false;
  /** Messages and frames received / sent, for debugging and tests. */
  readonly counts = { in: 0, out: 0, chunks: 0, unknown: 0 };

  private readonly conn: Connection;
  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly binHandlers = new Map<number, Set<(frame: Uint8Array) => void>>();
  private readonly pending = new Map<number, number[]>();
  private readonly remotes = new Map<string, Remote>();
  private readonly statusHandlers = new Set<(s: ServerStatus) => void>();
  private readonly dropHandlers = new Set<(retryMs: number, reason: string) => void>();
  /** Sockets lost (or never opened) since the last welcome: the attempt count a notice shows. */
  drops = 0;
  private seq = 1;
  private lastMoveAt = 0;
  private lastMove = '';
  private readonly now: () => number;
  private readonly key: string;

  constructor(opts: ServerSessionOptions) {
    this.name = opts.name;
    this.view = opts.view;
    this.key = opts.key;
    this.now = opts.now ?? (() => performance.now());
    this.conn = new Connection(opts.url, {
      open: () => this.hello(),
      text: (d) => this.onText(d),
      binary: (d) => this.onFrame(d),
      close: (retry, reason) => {
        this.spawned = false;
        this.drops++;
        if (this.status !== 'refused') this.setStatus(retry < 0 ? 'refused' : this.welcome ? 'reconnecting' : 'connecting');
        if (this.status !== 'refused') for (const h of [...this.dropHandlers]) { try { h(retry, reason); } catch (e) { console.error('[srv] drop handler', e); } }
      },
    }, opts.socket);
  }

  get url(): string { return this.conn.url; }
  get online(): boolean { return this.status === 'online'; }

  start(): void { this.conn.start(); }
  stop(): void { this.conn.stop(); }

  /** Every time the socket drops or fails to open while we keep trying: when the next attempt starts and the close reason. */
  onDrop(handler: (retryMs: number, reason: string) => void): () => void {
    this.dropHandlers.add(handler);
    return () => { this.dropHandlers.delete(handler); };
  }

  /** Resolves with the first welcome; rejects if the server refuses the login. */
  ready(): Promise<Welcome> {
    return new Promise((resolve, reject) => {
      if (this.welcome) { resolve(this.welcome); return; }
      if (this.status === 'refused') { reject(new Error(this.refusal)); return; }
      const off = this.onStatus((s) => {
        if (s === 'online' && this.welcome) { off(); resolve(this.welcome); }
        else if (s === 'refused') { off(); reject(new Error(this.refusal)); }
      });
    });
  }

  /** Hand over the world to keep up to date; what the server sent before this is delivered now. */
  attach(world: WorldPort): void {
    const held = this.world;
    this.world = world;
    if (held instanceof HeldWorld) held.drain(world);
  }

  // ---- subscribing -------------------------------------------------------

  /**
   * Subscribe to a server message by its `t`. Runs after the session's own
   * handling of that type (so on 'ack' the world is already corrected, on
   * 'welcome' `srv.welcome` is the new one). Returns the unsubscribe function.
   */
  on<T extends ServerMsg['t']>(type: T, handler: (m: MsgOf<T>) => void): () => void;
  on(type: string, handler: (m: AnyMsg) => void): () => void;
  on(type: string, handler: Handler): () => void {
    let set = this.handlers.get(type);
    if (!set) this.handlers.set(type, set = new Set());
    set.add(handler);
    return () => { set.delete(handler); };
  }

  /** Subscribe to binary frames whose first byte is `tag` (BIN_CHUNK is handled here as well). */
  onBinary(tag: number, handler: (frame: Uint8Array) => void): () => void {
    let set = this.binHandlers.get(tag);
    if (!set) this.binHandlers.set(tag, set = new Set());
    set.add(handler);
    return () => { set.delete(handler); };
  }

  onStatus(handler: (s: ServerStatus) => void): () => void {
    this.statusHandlers.add(handler);
    return () => { this.statusHandlers.delete(handler); };
  }

  // ---- sending -----------------------------------------------------------

  /** Send an intent. False if the socket is not open (dropped, not queued). */
  send(msg: ClientMsg): boolean { return this.sendRaw(msg); }

  /** Send an intent protocol.ts does not type yet. */
  sendRaw(msg: { t: string; [k: string]: unknown }): boolean {
    if (!this.conn.send(JSON.stringify(msg))) return false;
    this.counts.out++;
    return true;
  }

  /** A fresh sequence number for an intent the server answers with `ack`. */
  nextSeq(): number { return this.seq++; }

  /**
   * Apply the player's own block edit locally and remember what it changed
   * under a new seq. Send that seq with the intent; when the `ack` for it
   * says ok:false the cells are put back, and either way the acked cell is set
   * to what the server says.
   */
  predictBlock(x: number, y: number, z: number, id: number): number {
    const seq = this.nextSeq();
    this.track(seq, this.world.predict(x, y, z, id));
    return seq;
  }

  /** Remember cells [x, y, z, oldId, ...] a prediction changed some other way, to be put back if `seq` is refused. */
  track(seq: number, cells: number[]): void {
    this.pending.set(seq, cells);
    if (this.pending.size > MAX_PENDING) this.pending.delete(this.pending.keys().next().value as number);
  }

  /** Break the block at a cell: predicted now, confirmed or reverted by the ack. Returns the seq. */
  dig(x: number, y: number, z: number, predictedId = 0): number {
    const seq = this.predictBlock(x, y, z, predictedId);
    this.send({ t: 'dig', seq, x, y, z });
    return seq;
  }

  /** Put block `id` in a cell: predicted now, confirmed or reverted by the ack. Returns the seq. */
  place(x: number, y: number, z: number, id: number): number {
    const seq = this.predictBlock(x, y, z, id);
    this.send({ t: 'place', seq, x, y, z, id });
    return seq;
  }

  chat(text: string): void { this.send({ t: 'chat', text }); }

  /** Call every game tick: reports the local player at 20 Hz once spawned. */
  tick(p: LocalPose): void {
    if (!this.spawned || this.status !== 'online') return;
    const t = this.now();
    if (t - this.lastMoveAt < MOVE_INTERVAL_MS - 5) return;
    const f = (p.sneaking ? MF_SNEAK : 0) | (p.sprinting ? MF_SPRINT : 0) | (p.swimming ? MF_SWIM : 0)
      | (p.flying ? MF_FLY : 0) | (p.onGround ? MF_GROUND : 0) | (p.usingItem ? MF_USING : 0) | (p.glider?.gliding ? MF_GLIDE : 0);
    const r = (n: number): number => Math.round(n * 1000) / 1000;
    const msg: ClientMsg = { t: 'move', x: r(p.position.x), y: r(p.position.y), z: r(p.position.z), yaw: r(p.yaw), pitch: r(p.pitch), f };
    const text = JSON.stringify(msg);
    // standing still: one move a second is enough to say so
    if (text === this.lastMove && t - this.lastMoveAt < 1000) return;
    if (!this.conn.send(text)) return;
    this.counts.out++;
    this.lastMove = text;
    this.lastMoveAt = t;
  }

  // ---- other players -----------------------------------------------------

  /** Other players in our dimension, interpolated to now (the `source` net/remotePlayers.ts wants). */
  remotePlayers(out: ServerPlayerView[]): ServerPlayerView[] {
    out.length = 0;
    const t = this.now();
    for (const [id, r] of this.remotes) {
      if (t - r.lastAt > STALE_MS) continue;
      if (!r.buf.sample(t - INTERP_DELAY_MS, r.s)) continue;
      const v = r.v, s = r.s;
      v[0] = s[0]; v[1] = s[1]; v[2] = s[2];
      v[3] = s[3]; v[4] = s[3]; v[5] = s[4];
      v[6] = s[5]; v[7] = s[6]; v[8] = 0;
      const st = r.state;
      st.x = v[0]; st.y = v[1]; st.z = v[2]; st.yaw = v[3]; st.headYaw = v[4]; st.pitch = v[5];
      st.limbSwing = v[6]; st.limbSwingAmount = v[7];
      st.flags = r.flags & 15; // MF_GROUND shares a bit with the P2P "drawing a bow" flag
      out.push({ id, name: r.name, state: st, v, ping: 0, isHost: false });
    }
    return out;
  }

  /** Latest known row of another player (not interpolated), or null. */
  playerRow(id: string): { x: number; y: number; z: number; yaw: number; pitch: number; flags: number } | null {
    const r = this.remotes.get(id);
    const l = r?.buf.latest;
    if (!r || !l) return null;
    return { x: l.v[0], y: l.v[1], z: l.v[2], yaw: l.v[3], pitch: l.v[4], flags: r.flags };
  }

  // ---- receiving ---------------------------------------------------------

  private hello(): void {
    this.sendRaw({ t: 'hello', proto: PROTOCOL_VERSION, name: this.name, key: this.key, view: this.view });
  }

  private setStatus(s: ServerStatus): void {
    if (this.status === s) return;
    this.status = s;
    for (const h of [...this.statusHandlers]) { try { h(s); } catch (e) { console.error('[srv] status handler', e); } }
  }

  /**
   * Chunk frames decode asynchronously (inflate). Everything that arrives
   * while one is decoding waits behind it, so a `blocks` or `unload` for a
   * chunk is never handled before the chunk itself.
   */
  private inOrder(job: () => void | Promise<void>): void {
    if (this.busy === 0) {
      const r = job();
      if (!r) return;
      this.busy++;
      this.chain = r.then(() => { this.busy--; });
      return;
    }
    this.busy++;
    this.chain = this.chain.then(job).then(() => { this.busy--; });
  }
  private busy = 0;
  private chain: Promise<void> = Promise.resolve();

  private onText(data: string): void {
    let m: AnyMsg;
    try { m = JSON.parse(data) as AnyMsg; } catch { return; }
    if (!m || typeof m.t !== 'string') return;
    this.counts.in++;
    this.inOrder(() => this.dispatch(m));
  }

  /**
   * The page subscribes while it is being built (main.ts awaits assets after the login), and the server
   * starts talking at once: between `hold()` and `live()` what arrives is kept, in order, and handed to the subscribers
   * then. Without this the entities, slots and stats of the first second were lost for good.
   */
  private held: (AnyMsg | Uint8Array)[] | null = null;
  hold(): void { this.held ??= []; }
  live(): void {
    const held = this.held;
    this.held = null;
    if (!held) return;
    for (const m of held) { if (m instanceof Uint8Array) this.deliverBin(m); else this.deliver(m); }
  }

  private dispatch(m: AnyMsg): void {
    try { this.handle(m as unknown as ServerMsg); } catch (e) { console.error('[srv] handling', m.t, e); }
    if (this.held) {
      // a new login while loading: what was kept belongs to the old one
      if (m.t === 'welcome') this.held.length = 0; else this.held.push(m);
      return;
    }
    this.deliver(m);
  }

  private deliver(m: AnyMsg): void {
    const set = this.handlers.get(m.t);
    if (!set) { if (!KNOWN.has(m.t)) this.counts.unknown++; return; }
    for (const h of [...set]) {
      try { (h as (m: AnyMsg) => void)(m); } catch (e) { console.error('[srv] handler for', m.t, e); }
    }
  }

  private onFrame(frame: Uint8Array): void {
    if (frame.byteLength === 0) return;
    this.counts.in++;
    // inflate starts now, so a burst of chunks decodes side by side; only handing them over is in order
    const decoded = frame[0] === BIN_CHUNK ? decodeChunk(frame) : null;
    this.inOrder(() => {
      if (this.held && frame[0] !== BIN_CHUNK) this.held.push(frame); else this.deliverBin(frame);
      if (!decoded) return;
      return decoded.then((c) => {
        if (!c) return;
        this.counts.chunks++;
        try { this.world.chunk(c.dim, c.cx, c.cz, c.blocks); } catch (e) { console.error('[srv] chunk', e); }
      }, () => undefined);
    });
  }

  private deliverBin(frame: Uint8Array): void {
    const set = this.binHandlers.get(frame[0]);
    if (set) for (const h of [...set]) { try { h(frame); } catch (e) { console.error('[srv] binary handler', frame[0], e); } }
  }

  private handle(m: ServerMsg): void {
    switch (m.t) {
      case 'welcome': {
        this.welcome = m;
        this.drops = 0;
        this.id = m.id; this.seed = m.seed; this.wgv = m.wgv; this.dim = m.dim; this.time = m.time;
        this.names.clear();
        for (const p of m.players) this.names.set(p.id, p.name);
        this.names.set(m.id, this.name);
        this.remotes.clear();
        this.pending.clear(); // the server's chunks are the truth now
        this.spawned = false;
        this.lastMove = '';
        this.world.reset(m.dim);
        this.setStatus('online');
        break;
      }
      case 'refuse':
        this.refusal = m.why;
        this.setStatus('refused');
        this.conn.stop(); // a refusal is final (wrong version, full, logged in elsewhere): retrying would fight it
        break;
      case 'unload': this.world.unload(m.cx, m.cz); break;
      case 'blocks': {
        const e = m.e, w = this.world;
        w.batch(() => { for (let i = 0; i + 3 < e.length; i += 4) w.applyServer(e[i], e[i + 1], e[i + 2], e[i + 3]); });
        break;
      }
      case 'ack': {
        const cells = this.pending.get(m.seq);
        this.pending.delete(m.seq);
        const w = this.world;
        if (!m.ok && cells && cells.length > 0) w.revert(cells);
        w.applyServer(m.x, m.y, m.z, m.id);
        break;
      }
      case 'players': this.onPlayers(m.p); break;
      case 'join': this.names.set(m.id, m.name); break;
      case 'leave': this.names.delete(m.id); this.remotes.delete(m.id); break;
      case 'time': this.time = m.time; break;
      case 'pos':
        if (m.dim !== this.dim) { this.dim = m.dim; this.remotes.clear(); this.pending.clear(); }
        this.lastMove = '';
        break;
      default: break;
    }
  }

  private onPlayers(rows: PlayerRow[]): void {
    const t = this.now();
    for (const row of rows) {
      const id = row[0];
      if (id === this.id) continue;
      let r = this.remotes.get(id);
      // back after a gap: start a new track rather than gliding in from the old place
      if (r && t - r.lastAt > STALE_MS) { this.remotes.delete(id); r = undefined; }
      if (!r) {
        r = {
          name: '', buf: new SnapshotBuffer(7, [3]), state: emptyPlayerState(), v: new Float64Array(9), s: new Float64Array(7),
          lastAt: t, swing: 0, amt: 0, lx: row[1], lz: row[3], flags: 0,
        };
        this.remotes.set(id, r);
      }
      r.name = this.names.get(id) ?? 'Player';
      const dist = Math.hypot(row[1] - r.lx, row[3] - r.lz);
      r.lx = row[1]; r.lz = row[3];
      if (dist < 8) { // a teleport is not a stride
        r.swing += dist * 1.6;
        r.amt += (Math.min(1, dist * 4.5) - r.amt) * 0.5;
      }
      r.flags = row[6];
      r.lastAt = t;
      r.buf.push(t, [row[1], row[2], row[3], row[4], row[5], r.swing, r.amt]);
    }
  }
}

/** Chunks received before the page has a world to put them in. */
class HeldWorld implements WorldPort {
  private readonly chunks = new Map<string, { dim: DimensionId; cx: number; cz: number; blocks: Uint16Array }>();
  predict(): number[] { return []; }
  revert(): void { /* nothing predicted */ }
  applyServer(x: number, y: number, z: number, id: number): void {
    const c = this.chunks.get((x >> 4) + ',' + (z >> 4));
    // index as world/chunk.ts: x + z * 16 + y * 256
    if (c && y >= 0) { const i = (x & 15) + (z & 15) * 16 + y * 256; if (i < c.blocks.length) c.blocks[i] = id; }
  }
  batch(fn: () => void): void { fn(); }
  chunk(dim: DimensionId, cx: number, cz: number, blocks: Uint16Array): void { this.chunks.set(cx + ',' + cz, { dim, cx, cz, blocks }); }
  unload(cx: number, cz: number): void { this.chunks.delete(cx + ',' + cz); }
  reset(): void { this.chunks.clear(); }
  drain(to: WorldPort): void {
    for (const c of this.chunks.values()) to.chunk(c.dim, c.cx, c.cz, c.blocks);
    this.chunks.clear();
  }
}

const KNOWN = new Set(['welcome', 'refuse', 'unload', 'blocks', 'ack', 'players', 'join', 'leave', 'time', 'pos', 'chat']);
// client/entities.ts listens to these; they are not unknown while nobody has subscribed yet
for (const t of ['spawn', 'meta', 'despawn', 'collect', 'mount', 'offers', 'boss', 'lightning', 'weather']) KNOWN.add(t);
